import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { BaseViewConfig, BaseViewConfigPatch, ViewConfig } from 'types/contextView';
import { deepMergeJson } from '../utils/deepMergeJson';

const DEFAULT_VIEW_KEY = '__default__';

/** The session layer's contents for one `(contextRef, viewSystemName)` key — same shape `resolveViewConfig`'s `session` layer expects. */
export interface SessionLayer {
  base: Partial<BaseViewConfig>;
  view: Partial<ViewConfig>;
}

const EMPTY_LAYER: SessionLayer = { base: {}, view: {} };

function buildSessionKey(contextRef: string, viewSystemName?: string | null): string {
  return `${contextRef}::${viewSystemName ?? DEFAULT_VIEW_KEY}`;
}

/**
 * Merges a patch into a stored config-layer section, generically recursive
 * (`deepMergeJson`) rather than letting a second patch that only touches one
 * `Record<levelId, …>` key (`levelOverrides`, `columnState`, `levelFilters`,
 * whatever's next) replace the first patch's entries for every other key —
 * the same rule `resolveViewConfig` applies across layers, applied here
 * across successive patches to the same layer. No allow-list of which
 * fields are nested records: this matches Postgres `jsonb_deep_merge`
 * exactly, so any `Record<levelId, …>` field merges per key without being
 * named here first (see EAINT-781 Phase 3).
 *
 * Exported so `useViewPersonalization` (the user layer's own successive-patch
 * accumulation) can reuse this exact merge rather than writing a second copy.
 */
export function mergeSessionPatch(
  current: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  return deepMergeJson(current, patch);
}

export interface ViewState {
  /** The session-layer overlay for a `(contextRef, viewSystemName)` key — pass straight into `resolveViewConfig`'s `session` layer input. */
  getSession: (contextRef: string, viewSystemName?: string | null) => SessionLayer;
  /** Merges a base-config patch into the session layer, per-key for `levelOverrides`. */
  patchSessionBase: (contextRef: string, viewSystemName: string | null | undefined, patch: BaseViewConfigPatch) => void;
  /** Merges a view-config patch into the session layer, per-key for `columnState`. */
  patchSessionView: (contextRef: string, viewSystemName: string | null | undefined, patch: Partial<ViewConfig>) => void;
  /** Clears the session layer for one key — the resolved config falls back to `defaults → view → user`. */
  resetSession: (contextRef: string, viewSystemName?: string | null) => void;
  /**
   * Clears EVERY session layer belonging to one context, across all of its
   * views. Leaving a context is what makes its session state unreachable —
   * and session layers key on `(contextRef, viewSystemName)`, so clearing
   * only the last-active view's key strands the others under keys the new
   * context never addresses (EAINT-789 SC6).
   */
  resetSessionForContext: (contextRef: string) => void;
  /**
   * Clears every session layer regardless of context — the org/impersonation
   * "logout"-shaped switch, where the acting identity changes and nothing
   * about the previous identity's unsaved filters should compose into the
   * new one's `runtimeFilters`. See {@link resetAllViewStateSessions} for why
   * this also exists as a plain function.
   */
  resetAllSessions: () => void;
}

const ViewStateContext = createContext<ViewState | undefined>(undefined);

/**
 * Providers currently mounted, so `resetAllViewStateSessions` can reach them
 * from OUTSIDE the React tree they provide to.
 *
 * `useSchemaTypeCacheReset` — the org/impersonation invalidation hook — runs
 * in `App()`, which renders `<ViewStateProvider>` as a child of its own
 * return value. That makes `App` an ANCESTOR of the provider, never a
 * descendant, so `useContext(ViewStateContext)` called from `App` (directly
 * or via a hook it calls) can never see the value the provider hands to ITS
 * children — no matter where inside `App`'s JSX the provider is mounted.
 * `useContextPermissions`' `permissionInvalidation.ts` solves the identical
 * "invalidate from a call site that predates/isn't nested under the target"
 * problem with a module-scope epoch; this is the same shape, sized for a
 * plain reset rather than a re-fetch signal (each provider's own `setLayers`
 * already re-renders its own subtree, so no `useSyncExternalStore` epoch is
 * needed here — just a way to reach the setter from outside).
 */
const mountedResetters = new Set<() => void>();

/**
 * Resets every mounted `ViewStateProvider`'s session layers, regardless of
 * where the caller sits in the component tree. Call on org/impersonation
 * switch, alongside the other identity-scoped cache resets in
 * `useSchemaTypeCacheReset`.
 */
export function resetAllViewStateSessions(): void {
  mountedResetters.forEach((reset) => reset());
}

export interface ViewStateProviderProps {
  children: ReactNode;
}

/**
 * The session layer of the four-layer view-config resolver
 * (`defaults → view → user → session`) — in-memory, keyed by
 * `(contextRef, viewSystemName ?? '__default__')`. Deliberately not
 * TreeMap-local: a user who rearranges a board on one visualization and
 * switches to another for the same context expects the unsaved tweak to
 * still be there, and it is also where a future unsaved-filter tier lands.
 * See `specs/view-personalization-overlay.md` § The session layer.
 */
export const ViewStateProvider: React.FC<ViewStateProviderProps> = ({ children }) => {
  const [layers, setLayers] = useState<Record<string, SessionLayer>>({});

  const getSession = useCallback(
    (contextRef: string, viewSystemName?: string | null): SessionLayer =>
      layers[buildSessionKey(contextRef, viewSystemName)] ?? EMPTY_LAYER,
    [layers]
  );

  const patchSessionBase = useCallback(
    (contextRef: string, viewSystemName: string | null | undefined, patch: BaseViewConfigPatch) => {
      const key = buildSessionKey(contextRef, viewSystemName);
      setLayers((prev) => {
        const current = prev[key] ?? EMPTY_LAYER;
        const mergedBase = mergeSessionPatch(
          current.base as Record<string, unknown>,
          patch as Record<string, unknown>
        ) as Partial<BaseViewConfig>;
        return { ...prev, [key]: { ...current, base: mergedBase } };
      });
    },
    []
  );

  const patchSessionView = useCallback(
    (contextRef: string, viewSystemName: string | null | undefined, patch: Partial<ViewConfig>) => {
      const key = buildSessionKey(contextRef, viewSystemName);
      setLayers((prev) => {
        const current = prev[key] ?? EMPTY_LAYER;
        const mergedView = mergeSessionPatch(
          current.view as Record<string, unknown>,
          patch as Record<string, unknown>
        ) as Partial<ViewConfig>;
        return { ...prev, [key]: { ...current, view: mergedView } };
      });
    },
    []
  );

  const resetSession = useCallback((contextRef: string, viewSystemName?: string | null) => {
    const key = buildSessionKey(contextRef, viewSystemName);
    setLayers((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetSessionForContext = useCallback((contextRef: string) => {
    const prefix = `${contextRef}::`;
    setLayers((prev) => {
      const doomed = Object.keys(prev).filter((key) => key.startsWith(prefix));
      if (doomed.length === 0) return prev;
      const next = { ...prev };
      doomed.forEach((key) => delete next[key]);
      return next;
    });
  }, []);

  const resetAllSessions = useCallback(() => {
    setLayers((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, []);

  // Registers THIS provider instance so `resetAllViewStateSessions` (called
  // from outside the tree, see the module-scope docblock above) can reach it.
  // Un-registers on unmount so a stale entry never survives past a provider
  // that no longer exists (Storybook remounts, `ViewStateBoundary` teardown).
  useEffect(() => {
    mountedResetters.add(resetAllSessions);
    return () => {
      mountedResetters.delete(resetAllSessions);
    };
  }, [resetAllSessions]);

  const value = useMemo<ViewState>(
    () => ({
      getSession,
      patchSessionBase,
      patchSessionView,
      resetSession,
      resetSessionForContext,
      resetAllSessions,
    }),
    [getSession, patchSessionBase, patchSessionView, resetSession, resetSessionForContext, resetAllSessions]
  );

  return <ViewStateContext.Provider value={value}>{children}</ViewStateContext.Provider>;
};

/**
 * Reads and writes the session layer for one `(contextRef, viewSystemName)`
 * key. Used outside a `ViewStateProvider`, returns a safe, non-throwing
 * fallback (empty layer, no-op writers) — matching `useToolbarState`'s
 * graceful-fallback shape — so an isolated consumer (Storybook, a unit test
 * that doesn't need session state) degrades to "no session state" instead of
 * crashing.
 */
export function useViewState(contextRef: string, viewSystemName?: string | null) {
  const context = useContext(ViewStateContext);
  const session = context ? context.getSession(contextRef, viewSystemName) : EMPTY_LAYER;

  const patchBase = useCallback(
    (patch: BaseViewConfigPatch) => context?.patchSessionBase(contextRef, viewSystemName, patch),
    [context, contextRef, viewSystemName]
  );
  const patchView = useCallback(
    (patch: Partial<ViewConfig>) => context?.patchSessionView(contextRef, viewSystemName, patch),
    [context, contextRef, viewSystemName]
  );
  const reset = useCallback(
    () => context?.resetSession(contextRef, viewSystemName),
    [context, contextRef, viewSystemName]
  );

  return useMemo(() => ({ session, patchBase, patchView, reset }), [session, patchBase, patchView, reset]);
}

/**
 * Mounts a {@link ViewStateProvider} only when there is not already one above.
 *
 * The session layer must outlive any single visualization: a user who
 * rearranges a board in the TreeMap and switches to Atlas on the same context
 * expects the unsaved tweak to still be there
 * (`specs/view-personalization-overlay.md` § The session layer). That requires
 * the provider to sit on an ancestor that survives the switch — the app shell
 * or the route — not inside the visualization that unmounts.
 *
 * But a visualization organism must also work standing alone (Storybook,
 * read-only embeds, tests), and it cannot read a context it renders itself.
 * Wrapping unconditionally would solve the standalone case and break the
 * hoisted one, because the inner provider shadows the outer and the session
 * dies with the organism anyway — silently, since `useViewState` resolves
 * against the nearest provider and has no way to tell it is the wrong one.
 *
 * So: defer when a provider exists, supply one when it does not. An organism
 * wraps itself in this rather than in `ViewStateProvider` directly.
 */
export const ViewStateBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const existing = useContext(ViewStateContext);
  if (existing) return <>{children}</>;
  return <ViewStateProvider>{children}</ViewStateProvider>;
};

export default ViewStateContext;
